/**
 * <product-card> Component
 * Manages product card functionality including:
 * - Updates product links to include variant query parameter
 * - Updates quick-add form selects based on option attributes
 * - Triggers Embla carousel reinitialization when option values change
 * 
 * This component:
 * - Finds all <product-card> elements with data-variant-id attributes
 * - Updates all links within each product card that point to /products/* URLs
 * - Updates select[name="id"] in quick-add forms based on data-option-index and data-option-value
 * - Triggers Embla reinitialization when data-option-value changes (CSS handles image visibility)
 */

(function() {
  'use strict';

  const initialisedCards = new WeakSet();
  const queuedDeferredCards = new WeakSet();
  const deferredQueue = [];
  let deferredProcessingScheduled = false;
  let lazyIntersectionObserver = null;
  
  /**
   * Updates a URL to include or replace the variant query parameter
   * @param {string} url - The original URL
   * @param {string} variantId - The variant ID to add
   * @returns {string} The updated URL with variant parameter
   */
  function updateUrlWithVariant(url, variantId) {
    if (!url || !variantId) return url;

    try {
      // Parse the URL
      const urlObj = new URL(url, window.location.origin);
      
      // Set or replace the variant parameter
      urlObj.searchParams.set('variant', variantId);
      
      // Return the updated URL (relative path)
      return urlObj.pathname + urlObj.search;
    } catch (e) {
      // Silently return original URL if parsing fails
      return url;
    }
  }

  /**
   * Updates all product links and external-popover elements within a product card element
   * @param {HTMLElement} productCard - The product card element
   */
  function updateProductCardLinks(productCard) {
    // Skip static cards - they're pre-rendered and don't need JS updates
    if (productCard.hasAttribute('data-static')) {
      return;
    }

    const variantId = productCard.getAttribute('data-variant-id');

    if (!variantId) {
      return; // Skip if no variant ID
    }

    // Find all links within the product card that point to /products/*
    const links = productCard.querySelectorAll('a[href*="/products/"]');
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      
      // Only process links that match /products/* pattern
      if (href && href.match(/\/products\/[^\/\s"']+/)) {
        const updatedHref = updateUrlWithVariant(href, variantId);
        if (updatedHref !== href) {
          link.setAttribute('href', updatedHref);
        }
      }
    });

    // Find all external-popover elements within the product card that have data-url pointing to /products/*
    const externalPopovers = productCard.querySelectorAll('external-popover[data-url*="/products/"]');
    
    externalPopovers.forEach(popover => {
      const dataUrl = popover.getAttribute('data-url');
      
      // Only process URLs that match /products/* pattern
      if (dataUrl && dataUrl.match(/\/products\/[^\/\s"']+/)) {
        const updatedUrl = updateUrlWithVariant(dataUrl, variantId);
        if (updatedUrl !== dataUrl) {
          // Update the data-url attribute
          // The external-popover component now uses a getter that reads from dataset.url dynamically,
          // so it will automatically use the updated URL when the user interacts with it
          popover.setAttribute('data-url', updatedUrl);
          // Also update dataset for consistency
          popover.dataset.url = updatedUrl;
        }
      }
    });
  }

  /**
   * Finds the scoped container for an element.
   * Scope is either product-card or null (main page)
   * @param {HTMLElement} element - The element to find the scope container for
   * @returns {HTMLElement|null}
   */
  function findScopeContainer(element) {
    if (!element) return null;
    return element.closest('product-card') || null;
  }

  /**
   * Queries for elements, scoped to the same container as the source element
   * If source is in a modal/drawer, only searches within that container
   * If source is in main document, excludes modals/drawers from search
   * @param {HTMLElement} sourceElement - The element to scope from
   * @param {string} selector - The CSS selector to query
   * @returns {HTMLElement[]}
   */
  function queryScopedElements(sourceElement, selector) {
    const scopeContainer = findScopeContainer(sourceElement);

    if (scopeContainer) {
      // Element is inside a scoped container - only search within it
      return Array.from(scopeContainer.querySelectorAll(selector));
    }

    // Element is in main document - search globally but exclude scoped containers
    const allElements = Array.from(document.querySelectorAll(selector));
    return allElements.filter(el => !findScopeContainer(el));
  }

  /**
   * Updates quick-add form selects based on product-card's option attributes
   * Finds select[name="id"] elements and selects the option matching
   * data-option-{index} attribute based on product-card's data-option-index and data-option-value
   *
   * @param {HTMLElement} productCard - The product card element
   */
  function updateProductCardQuickAddSelects(productCard) {
    // Skip static cards - they're pre-rendered and don't need JS updates
    if (productCard.hasAttribute('data-static')) {
      return;
    }

    const optionIndex = productCard.getAttribute('data-option-index');
    const optionValue = productCard.getAttribute('data-option-value');
    const productId = productCard.getAttribute('data-product-id');

    // Skip if required attributes are missing
    if (!optionIndex || !optionValue) {
      return;
    }

    // Build the data attribute name (e.g., "data-option-1")
    const dataAttributeName = `data-option-${optionIndex}`;

    // Collect all relevant select elements (scoped to same container context)
    let selects = [];

    // Strategy 1: Find selects within the product-card itself
    const cardSelects = productCard.querySelectorAll('select[name="id"]');
    selects = Array.from(cardSelects);

    // Strategy 2: If no selects found in card, find product-options components with matching product ID (scoped)
    if (selects.length === 0 && productId) {
      const productOptionsComponents = queryScopedElements(productCard, `product-options[data-product-id="${productId}"]`);
      productOptionsComponents.forEach(component => {
        const componentSelects = component.querySelectorAll('select[name="id"]');
        selects = selects.concat(Array.from(componentSelects));
      });
    }

    // Strategy 3: If still no selects, find all selects in quick-add forms (scoped fallback)
    if (selects.length === 0) {
      const quickAddForms = queryScopedElements(productCard, '[data-quick-add-forms="true"]');
      quickAddForms.forEach(container => {
        const containerSelects = container.querySelectorAll('select[name="id"]');
        selects = selects.concat(Array.from(containerSelects));
      });
    }

    // Update each select to select the matching option
    selects.forEach(select => {
      const options = select.querySelectorAll('option');
      
      // Find the option with matching data-option-{index} attribute
      let matchingOption = null;
      options.forEach(option => {
        const optionDataValue = option.getAttribute(dataAttributeName);
        if (optionDataValue === optionValue) {
          matchingOption = option;
        }
      });
      
      // If found, select it
      if (matchingOption) {
        select.value = matchingOption.value;
        
        // Update submit button state for this quick-add form
        const form = select.closest('form[action*="/cart/add"]');
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]');
          if (submitButton) {
            // Get inventory data from the selected option
            const maxQuantity = matchingOption.getAttribute('data-max-quantity');
            const inventoryPolicy = matchingOption.getAttribute('data-inventory-policy');
            
            // Check if variant is out of stock AND inventory policy denies selling
            const isOutOfStock = maxQuantity && parseInt(maxQuantity, 10) <= 0;
            const deniesBackorders = inventoryPolicy === 'deny';
            const shouldDisable = isOutOfStock && deniesBackorders;
            
            if (shouldDisable) {
              submitButton.disabled = true;
              submitButton.setAttribute('data-out-of-stock', 'true');
              submitButton.classList.add('opacity-25', 'cursor-not-allowed');
            } else {
              submitButton.disabled = false;
              submitButton.removeAttribute('data-out-of-stock');
              submitButton.classList.remove('opacity-25', 'cursor-not-allowed');
            }
          }
        }
        
        // Trigger change event in case other code is listening
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Updates a single product card (links and quick-add selects)
   * @param {HTMLElement} productCard - The product card element
   */
  function updateProductCard(productCard) {
    updateProductCardLinks(productCard);
    updateProductCardQuickAddSelects(productCard);
  }


  /**
   * Cleans up any deferred tracking for a product card
   * @param {HTMLElement} card
   */
  function cleanupDeferredCard(card) {
    if (!card || !(card instanceof HTMLElement)) {
      return;
    }

    if (queuedDeferredCards.has(card)) {
      queuedDeferredCards.delete(card);
    }

    if (lazyIntersectionObserver) {
      try {
        lazyIntersectionObserver.unobserve(card);
      } catch (e) {
        // Ignore errors from unobserving nodes that are no longer observed
      }
    }
  }

  /**
   * Forces an update of a product card regardless of initialisation state
   * @param {HTMLElement} card
   */
  function updateCard(card) {
    if (!card || !(card instanceof HTMLElement)) {
      return;
    }

    updateProductCard(card);
    initialisedCards.add(card);
    cleanupDeferredCard(card);
  }

  /**
   * Performs the initial staged setup for a product card exactly once
   * @param {HTMLElement} card
   */
  function runInitialCardSetup(card) {
    if (!card || !(card instanceof HTMLElement)) {
      return;
    }

    if (initialisedCards.has(card)) {
      cleanupDeferredCard(card);
      return;
    }

    updateCard(card);
  }

  /**
   * Lazily creates an IntersectionObserver to accelerate offscreen cards
   * @returns {IntersectionObserver|null}
   */
  function ensureIntersectionObserver() {
    if (lazyIntersectionObserver || typeof IntersectionObserver === 'undefined') {
      return lazyIntersectionObserver;
    }

    lazyIntersectionObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          runInitialCardSetup(entry.target);
        }
      });
    }, {
      root: null,
      rootMargin: '50% 0px 50% 0px',
      threshold: 0
    });

    return lazyIntersectionObserver;
  }

  /**
   * Queues a product card for deferred sequential processing
   * @param {HTMLElement} card
   */
  function enqueueDeferredCard(card) {
    if (!card || !(card instanceof HTMLElement)) {
      return;
    }

    if (initialisedCards.has(card) || queuedDeferredCards.has(card)) {
      return;
    }

    queuedDeferredCards.add(card);
    deferredQueue.push(card);

    const observer = ensureIntersectionObserver();
    if (observer) {
      observer.observe(card);
    }
  }

  /**
   * Schedules the next deferred product card to be processed on the next frame
   */
  function scheduleDeferredProcessing() {
    if (deferredProcessingScheduled || deferredQueue.length === 0) {
      return;
    }

    deferredProcessingScheduled = true;

    requestAnimationFrame(() => {
      deferredProcessingScheduled = false;

      if (deferredQueue.length === 0) {
        return;
      }

      const nextCard = deferredQueue.shift();
      runInitialCardSetup(nextCard);

      if (deferredQueue.length > 0) {
        scheduleDeferredProcessing();
      }
    });
  }

  /**
   * Determines if a bounding client rect is currently within the viewport
   * @param {DOMRect} rect
   * @param {number} viewportHeight
   * @returns {boolean}
   */
  function isRectInViewport(rect, viewportHeight) {
    if (!rect) {
      return false;
    }

    const vpHeight = viewportHeight || window.innerHeight || document.documentElement.clientHeight || 0;
    if (vpHeight === 0) {
      return false;
    }

    return rect.top < vpHeight && rect.bottom > 0;
  }

  /**
   * Classifies product cards into immediate, near, and deferred buckets
   * and schedules their staged processing
   * @param {HTMLElement[]} cards
   */
  function classifyAndScheduleCards(cards) {
    if (!cards || cards.length === 0) {
      return;
    }

    const uniqueCards = Array.from(new Set(cards.filter(card => card instanceof HTMLElement)));
    if (uniqueCards.length === 0) {
      return;
    }

    requestAnimationFrame(() => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const nearThreshold = viewportHeight * 0.5;
      const nearCards = [];

      uniqueCards.forEach(card => {
        if (initialisedCards.has(card)) {
          return;
        }

        const rect = card.getBoundingClientRect();

        if (isRectInViewport(rect, viewportHeight)) {
          runInitialCardSetup(card);
        } else if (rect.top < viewportHeight + nearThreshold) {
          nearCards.push(card);
        } else {
          enqueueDeferredCard(card);
        }
      });

      const hasDeferredCards = deferredQueue.length > 0;

      if (nearCards.length > 0) {
        requestAnimationFrame(() => {
          nearCards.forEach(runInitialCardSetup);

          if (hasDeferredCards) {
            scheduleDeferredProcessing();
          }
        });
      } else if (hasDeferredCards) {
        requestAnimationFrame(() => {
          scheduleDeferredProcessing();
        });
      }
    });
  }

  /**
   * Processes all existing product cards using staged scheduling
   */
  function processInitialCardsOnLoad() {
    const productCards = document.querySelectorAll('product-card');
    if (!productCards.length) {
      return;
    }

    classifyAndScheduleCards(Array.from(productCards));
    
    // Note: Carousel reinit is handled by variant-media and product-options components
    // which add/remove the 'hidden' class and trigger reinit automatically
    // No need to manually reinit here - pagination will be created correctly on initial load
  }

  /**
   * Collects product cards from a node and its descendants
   * @param {Node} node
   * @returns {HTMLElement[]}
   */
  function collectProductCardsFromNode(node) {
    const results = [];

    if (!node || node.nodeType !== Node.ELEMENT_NODE) {
      return results;
    }

    if (node.tagName && node.tagName.toLowerCase() === 'product-card') {
      results.push(node);
    }

    const descendants = node.querySelectorAll ? node.querySelectorAll('product-card') : [];
    descendants.forEach(descendant => {
      results.push(descendant);
    });

    return results;
  }

  /**
   * Forces an update for all product cards (used for variant change events)
   * Skips static cards that don't need JS updates
   */
  function refreshAllProductCards() {
    const cards = document.querySelectorAll('product-card[data-variant-id]:not([data-static]), product-card[data-option-index][data-option-value]:not([data-static])');
    if (!cards.length) {
      return;
    }

    cards.forEach(card => {
      updateCard(card);
    });
  }

  /**
   * Initialize the component
   */
  function init() {
    const handleReady = () => {
      processInitialCardsOnLoad();
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', handleReady);
    } else {
      handleReady();
    }

    const observer = new MutationObserver(mutations => {
      const newlyAddedCards = new Set();

      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          const target = mutation.target;

          if (target && target.tagName && target.tagName.toLowerCase() === 'product-card') {
            if (mutation.attributeName === 'data-variant-id') {
              updateCard(target);
            } else if (mutation.attributeName === 'data-option-index' || mutation.attributeName === 'data-option-value') {
              updateProductCardQuickAddSelects(target);
              initialisedCards.add(target);
              
              // Dispatch non-bubbling custom event for quick-add-forms component
              // Note: Embla reinit is handled by variant-media and product-options components
              // which add/remove the 'hidden' class and trigger reinit automatically
              if (mutation.attributeName === 'data-option-value') {
                const event = new CustomEvent('product-card:option-changed', {
                  bubbles: false, // Non-bubbling as requested
                  cancelable: false,
                  detail: {
                    optionValue: target.getAttribute('data-option-value'),
                    productCard: target
                  }
                });
                target.dispatchEvent(event);
              }
            }
          }
        }

        mutation.addedNodes.forEach(node => {
          const cards = collectProductCardsFromNode(node);
          cards.forEach(card => newlyAddedCards.add(card));
        });
      });

      if (newlyAddedCards.size > 0) {
        classifyAndScheduleCards(Array.from(newlyAddedCards));
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-variant-id', 'data-option-index', 'data-option-value']
    });

    document.addEventListener('product-options:changed', () => {
      requestAnimationFrame(() => {
        refreshAllProductCards();
      });
    });
  }

  // Initialize when script loads
  init();
})();

